import React from 'react';

import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

type CheckboxListProps<T> = {
  emptyMessage?: string;
  footnote?: string;
  items: Array<T>;
  label: (item: T) => string;
  onChange: (selected: Array<T>) => void;
  selected: Array<T>;
  title: string;
};

export default function CheckboxList<T extends string | number>({
  emptyMessage,
  footnote,
  items,
  label,
  onChange,
  selected,
  title,
}: CheckboxListProps<T>) {
  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <Box border={1} borderColor="divider" borderRadius={1} mt={2} p={1.5}>
      <Typography fontWeight={500} variant="body2">
        {title}
      </Typography>

      {items.length === 0 && emptyMessage ? (
        <Typography color="text.secondary" fontStyle="italic" mt={1} variant="body2">
          {emptyMessage}
        </Typography>
      ) : (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                onChange={(e) => onChange(e.target.checked ? [...items] : [])}
              />
            }
            label="Select all"
          />
          <Box display="flex" flexDirection="column" maxHeight={180} sx={{ overflowY: 'auto' }}>
            {items.map((item) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(item)}
                    onChange={(e) =>
                      onChange(e.target.checked ? [...selected, item] : selected.filter((value) => value !== item))
                    }
                  />
                }
                key={item}
                label={label(item)}
              />
            ))}
          </Box>
          {footnote && (
            <Typography color="text.secondary" display="block" mt={0.5} variant="caption">
              {footnote}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
